function afterLayout () {
  // Wait for two animation frames so the browser has:
  // 1) attached the offscreen SVG to the DOM
  // 2) performed layout with final fonts/kerning/transforms
  // This makes following getBoundingClientRect() calls reliable
  return new Promise(resolve =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  )
}

export async function analyzeQrSvg (svgString, { widthPx, heightPx } = {}) {
  // Parse the incoming SVG string into a real <svg> element (proper SVG namespace)
  const parsed = new DOMParser().parseFromString(svgString, 'image/svg+xml')
  const srcSvg = parsed.documentElement
  if (!srcSvg || srcSvg.nodeName.toLowerCase() !== 'svg') throw new Error('invalid svg')
  // Import the parsed node into this document so layout/measurement works
  const svg = document.importNode(srcSvg, true)

  // Mount the SVG offscreen so it gets fully laid out but remains invisible
  // Use opacity: 0 (not visibility: hidden) so text boxes are computed
  const lab = document.createElement('div')
  Object.assign(lab.style, {
    position: 'absolute',
    left: '-10000px',
    top: '0',
    opacity: '0',
    pointerEvents: 'none',
    contain: 'layout style' // isolate from page CSS as much as possible
  })
  lab.appendChild(svg)
  document.body.appendChild(lab)

  try {
    // Optionally force a specific rendered size (e.g., match your <img> CSS size)
    // If omitted, the SVG's intrinsic width/height will be used
    if (widthPx) svg.style.width = `${widthPx}px`
    if (heightPx) svg.style.height = `${heightPx}px`

    // Ensure webfonts (if any) are loaded before measuring text
    if (document.fonts?.ready) { try { await document.fonts.ready } catch {} }
    // Ensure layout is committed
    await afterLayout()

    // Root SVG viewport box in CSS pixels
    const svgRect = svg.getBoundingClientRect()

    // For each <text>, compute its axis-aligned bounding box after all transforms
    // and check which sides (if any) cross the SVG viewport
    const texts = Array.from(svg.querySelectorAll('text')).map((text, index) => {
      const rect = text.getBoundingClientRect()
      const edges = {
        left: rect.left < svgRect.left,
        right: rect.right > svgRect.right,
        top: rect.top < svgRect.top,
        bottom: rect.bottom > svgRect.bottom
      }
      return {
        index,
        id: text.id || null,
        text: (text.textContent || '').replace(/\s+/g, ' ').trim(),
        overflow: edges.left || edges.right || edges.top || edges.bottom,
        edges,
        rect: { x: rect.left, y: rect.top, width: rect.width, height: rect.height }
      }
    })

    // find the QR square:
    // Prefer explicit IDs first (#qr-group or #qr-code), then loose "contains 'qr'" matches
    const qrEl = svg.querySelector('#qr-group') || svg.querySelector('#qr-code') ||
               svg.querySelector('g[id*="qr" i], path[id*="qr" i], rect[id*="qr" i]')

    // fallback: approximate QR box as the union of non-text shapes
    // (exclude the background and anything inside #text-group)
    if (!qrEl) {
      const shapes = Array.from(svg.querySelectorAll('path,rect,g'))
        .filter(el => el.id !== 'background' && !el.closest('#text-group'))
      if (shapes.length) {
        // Union all candidate shapes' rects
        const first = shapes[0].getBoundingClientRect()
        let minL = first.left
        let minT = first.top
        let maxR = first.right
        let maxB = first.bottom
        for (let i = 1; i < shapes.length; i++) {
          const rect = shapes[i].getBoundingClientRect()
          minL = Math.min(minL, rect.left)
          minT = Math.min(minT, rect.top)
          maxR = Math.max(maxR, rect.right)
          maxB = Math.max(maxB, rect.bottom)
        }
        // Normalize that union rect to percentages of the SVG viewport
        const qrRect = { left: minL, top: minT, right: maxR, bottom: maxB, width: maxR - minL, height: maxB - minT }
        const qrBoundingBox = normalize(qrRect, svgRect)
        // Return overflow info + normalized QR rect (0..1)
        return { texts, qrBoundingBox }
      }
    }

    // If we found a specific QR element, normalize its rect to 0..1 as well
    const qrRect = qrEl ? qrEl.getBoundingClientRect() : null
    const qrBoundingBox = qrRect ? normalize(qrRect, svgRect) : null
    return { texts, qrBoundingBox }
  } finally {
    // Always clean up the offscreen mount
    lab.remove()
  }

  // Convert an absolute rect in CSS pixels to normalized coordinates within the SVG viewport
  // x,y,w,h in [0..1], plus center (cx, cy) for convenient centering
  function normalize (rect, root) {
    const x = (rect.left - root.left) / root.width
    const y = (rect.top - root.top) / root.height
    const w = rect.width / root.width
    const h = rect.height / root.height
    return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 }
  }
}
